/* eslint-disable react/no-unstable-nested-components */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useNotifications } from 'features/notifications';
import ScanField from 'features/scan/components/ScanField';
import { ScanTypes } from 'features/scan/utils/parseScanCode';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { getShippingEstimateText } from 'utils/format';

import AddressDisplay from './addressDisplay';
import { usePackage } from './packageProvider';
import { useShipping } from './shippingProvider';

export default function PackingSlipForm({ onNext }) {
  const {
    addNotification,
    removeNotification,
  } = useNotifications();
  const {
    order,
    selectedShipment,
    setSelectedShipment,
    totalPkgNum,
    packages,
    setPackages,
    setTotalPkgNum,
    shipDate,
    setShipDate,
  } = usePackage();
  const {
    isLoading,
    toAddress,
    setToAddress,
    handleVerifyAddress,
  } = useShipping();

  // Line items whose total shipped (previous shipments + all packages on this
  // form) won't equal the quantity ordered. Only items checked in at least one
  // package are considered — items deliberately left out of this shipment
  // shouldn't warn. Non-blocking: partial/over-shipping is allowed, just flagged.
  const totalQtyMismatches = useMemo(() => {
    if (!selectedShipment) return [];
    return selectedShipment.lineItems
      .map((lineItem) => {
        const checkedItems = packages
          .flatMap((pkg) => pkg.lineItems)
          .filter((li) => li.id === lineItem.id && li.isChecked);
        if (checkedItems.length === 0) return null;
        const alreadyShipped = lineItem.quantityShipped || 0;
        const shippingNow = checkedItems.reduce(
          (sum, li) => sum + (li.quantityShipped || 0),
          0,
        );
        const total = alreadyShipped + shippingNow;
        if (total === lineItem.quantity) return null;
        return {
          id: lineItem.id,
          name: lineItem.name || lineItem.projectId,
          quantity: lineItem.quantity,
          alreadyShipped,
          shippingNow,
          total,
        };
      })
      .filter(Boolean);
  }, [selectedShipment, packages]);

  const handleNextPage = useCallback(() => {
    // check all packages has checked items
    removeNotification('missing-package-item');

    if (!totalPkgNum) {
      addNotification({
        type: 'error',
        content: 'Please enter Total Package Number.',
        dismissible: true,
        id: 'missing-package-item',
      });
      return;
    }

    const allChecked = packages.every((pkg) => pkg.lineItems.some(
      (item) => item.isChecked && item.quantityShipped > 0,
    ));
    if (!allChecked) {
      addNotification({
        type: 'error',
        content: 'Please select at least one item in each package.',
        dismissible: true,
        id: 'missing-package-item',
      });
      return;
    }
    // verify address
    handleVerifyAddress({ onSuccess: onNext });
  }, [removeNotification, totalPkgNum, packages, handleVerifyAddress, onNext, addNotification]);

  return (
    <SpaceBetween size="m">
      <Container
        header={<Header>Packing Slip</Header>}
      >
        <SpaceBetween size="m">
          {order.shipments.length > 1 && !!selectedShipment && (
            <FormField label="Select Shipment">
              <Select
                selectedOption={{
                  value: selectedShipment.index,
                  label: `${selectedShipment.name} (${selectedShipment.targetShipDate})`,
                }}
                onChange={({ detail }) => {
                  const idx = detail.selectedOption.value;
                  setSelectedShipment({ ...order.shipments[idx], index: idx });
                }}
                options={order.shipments.map((s, index) => ({
                  value: index,
                  label: `${s.name} (${s.targetShipDate})`,
                }))}
              />
            </FormField>
          )}

          <FormField label="Shipping Address">
            <AddressDisplay
              backupAddress={selectedShipment?.address}
              address={toAddress}
              onEdit={setToAddress}
            />
          </FormField>

          <ColumnLayout columns={1}>
            <FormField
              label="Total Package Number"
              constraintText={getShippingEstimateText(order.shipCharged, order.packageCount)}
            >
              <Input
                type="number"
                value={totalPkgNum}
                onChange={({ detail }) => setTotalPkgNum(parseInt(detail.value, 10) || null)}
              />
            </FormField>
          </ColumnLayout>

          <FormField
            label="Ship Date"
            errorText={!shipDate && 'Please enter Ship Date'}
            constraintText={selectedShipment?.targetShipDate
              ? `Target Ship Date: ${selectedShipment.targetShipDate}`
              : ''}
          >
            <Input
              value={shipDate}
              onChange={({ detail }) => setShipDate(detail.value)}
            />
          </FormField>
        </SpaceBetween>
      </Container>

      {packages.map((pkg, pkgIdx) => {
        // All traveler IDs already claimed anywhere on the form.
        // Computed here so the cell renderers below can close over the latest value.
        const allScannedIds = packages.flatMap(
          (p) => p.lineItems.flatMap((li) => li.travelerIds ?? []),
        );

        // Checked items where travelers were scanned but Qty Shipped disagrees
        // with the scan count (e.g. qty was edited manually after scanning).
        const scanMismatches = pkg.lineItems.filter((li) => li.isChecked
          && (li.travelerIds ?? []).length > 0
          && li.quantityShipped !== li.travelerIds.length);

        return (
          // eslint-disable-next-line react/no-array-index-key
          <SpaceBetween key={pkgIdx} size="xs">
            <Table
              header={<Header variant="h3">{`Package ${pkgIdx + 1} Items`}</Header>}
              wrapLines
              columnDefinitions={[
                {
                  id: 'checkBox',
                  header: '',
                  cell: (item) => (
                    <Checkbox
                      checked={item.isChecked}
                      onChange={() => {
                        setPackages((prev) => prev.map((p, i) => {
                          if (i !== pkgIdx) return p;
                          return {
                            ...p,
                            lineItems: p.lineItems.map((li) => (
                              li.id === item.id ? { ...li, isChecked: !li.isChecked } : li
                            )),
                          };
                        }));
                      }}
                    />
                  ),
                },
                {
                  id: 'nameDisplay',
                  header: 'Description',
                  isRowHeader: true,
                  maxWidth: 200,
                  cell: (item) => (
                    <div>
                      <div>{item.name}</div>
                      {item.description && (
                      <Box color="text-status-inactive" variant="small">
                        {item.description}
                      </Box>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'quantity',
                  header: 'Qty Ordered',
                  cell: (item) => item.quantity,
                },
                {
                // Number input + scanned traveler IDs listed one per line below it
                  id: 'quantityShipped',
                  header: 'Qty Shipped',
                  cell: (item) => (
                    <SpaceBetween size="xs">
                      <Input
                        value={item.quantityShipped}
                        type="number"
                        onChange={({ detail }) => {
                          setPackages((prev) => prev.map((p, i) => {
                            if (i !== pkgIdx) return p;
                            return {
                              ...p,
                              lineItems: p.lineItems.map((li) => (
                                li.id === item.id
                                  ? { ...li, quantityShipped: parseInt(detail.value, 10) || null }
                                  : li
                              )),
                            };
                          }));
                        }}
                      />
                      {(item.travelerIds ?? []).map((tid) => (
                        <SpaceBetween
                          key={tid}
                          direction="horizontal"
                          size="xxs"
                          alignItems="center"
                        >
                          <Link href={`/travelers/${tid}`} fontSize="body-s" external>
                            {`${tid.substring(0, 8)}…`}
                          </Link>
                          <Button
                            iconName="close"
                            variant="inline-icon"
                            ariaLabel={`Remove traveler ${tid}`}
                            onClick={() => {
                              setPackages((prev) => prev.map((p, i) => {
                                if (i !== pkgIdx) return p;
                                return {
                                  ...p,
                                  lineItems: p.lineItems.map((li) => {
                                    if (li.id !== item.id) return li;
                                    const nextIds = (li.travelerIds ?? [])
                                      .filter((id) => id !== tid);
                                    return {
                                      ...li,
                                      travelerIds: nextIds,
                                      // Keep qty in sync with scan count, same as onScan
                                      quantityShipped: nextIds.length,
                                    };
                                  }),
                                };
                              }));
                            }}
                          />
                        </SpaceBetween>
                      ))}
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'scan',
                  header: 'Scan Travelers',
                  cell: (item) => (
                    <ScanField
                      id={`traveler-scan-${pkgIdx}-${item.id}`}
                      buttonText=""
                      accept={[ScanTypes.TRAVELER]}
                      onScan={({ id: travelerId }) => {
                      // Ignore if already scanned anywhere on this form
                        if (allScannedIds.includes(travelerId)) return;
                        setPackages((prev) => prev.map((p, i) => {
                          if (i !== pkgIdx) return p;
                          return {
                            ...p,
                            lineItems: p.lineItems.map((li) => {
                              if (li.id !== item.id) return li;
                              const ids = li.travelerIds ?? [];
                              if (ids.includes(travelerId)) return li;
                              const nextIds = [...ids, travelerId];
                              return {
                                ...li,
                                travelerIds: nextIds,
                                // Auto-fill qty from scan count; user can still override
                                quantityShipped: nextIds.length,
                              };
                            }),
                          };
                        }));
                      }}
                    />
                  ),
                },
              ]}
              items={pkg.lineItems}
            />
            {scanMismatches.length > 0 && (
              <Alert type="warning" header={`Package ${pkgIdx + 1}: Qty Shipped doesn't match scanned travelers`}>
                {scanMismatches.map((li) => (
                  <div key={li.id}>
                    {`${li.name}: Qty Shipped is ${li.quantityShipped ?? 0}, but ${li.travelerIds.length} traveler(s) scanned.`}
                  </div>
                ))}
              </Alert>
            )}
          </SpaceBetween>
        );
      })}

      {totalQtyMismatches.length > 0 && (
        <Alert type="warning" header="Total shipped quantity doesn't match quantity ordered">
          {totalQtyMismatches.map((item) => (
            <div key={item.id}>
              {`${item.name}: previously shipped ${item.alreadyShipped} + this shipment ${item.shippingNow} = ${item.total}, but ${item.quantity} ordered.`}
            </div>
          ))}
        </Alert>
      )}

      <div style={{ textAlign: 'right' }}>
        <Button variant="primary" loading={isLoading} onClick={() => handleNextPage()}>Next</Button>
      </div>
    </SpaceBetween>
  );
}

PackingSlipForm.propTypes = {
  onNext: PropTypes.func.isRequired,
};
